import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import { withDataProvider } from 'ra-core';
import { Form } from 'react-final-form';
import { withStyles } from '@material-ui/core';
import EditPageTitle from '../../../../components/EditPageTitle';
import LanguagesList from '../../../../components/LanguagesList';
import ToolbarButtonsStyles from '../../../../components/ToolbarButtonsStyles';
import LanguageFields from './LanguageFields';
import { sendCustomApiRequest } from '../../../../dataProvider';
import LoadingIndicator from '../../../../components/LoadingIndicator';

/**
 * @typedef UniversalConsentItemTypeTranslation
 * @type {object}
 * @property {string} languages language url
 * @property {string} longText
 * @property {string} shortText
 */

const styles = {
  ...ToolbarButtonsStyles,
  form: {
    flex: 1,
    padding: '24px 60px',
  },
  toolbar: {
    width: '100%',
    maxWidth: '1060px',
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  hidden: {
    display: 'none',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  languagesWrapper: {
    width: '248px',
    minHeight: '100%',
    borderRight: '1px solid #EDEDED',
    backgroundColor: 'white',
  },
  loader: {
    height: '80vh',
  },
};

const Languages = ({ classes, onSave, data, dataProvider, onBack }) => {
  const [chosenLanguage, setChosenLanguage] = useState('');
  const [info, setInfo] = useState({ name: '', languagesIDs: [], universalConsentItemTranslations: [] });
  const [isInvalid, setIsInvalid] = useState(true);
  const [languagesData, setLanguagesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const onLanguagePick = language => {
    setChosenLanguage(language.id);
  };
  const onLanguageDelete = ({ id }, values) => {
    const data = values.universalConsentItemTranslations.reduce((result, current) => {
      if (current.language !== id) {
        result.languagesIDs.push(current.language);
        result.universalConsentItemTranslations.push(current);
      }
      return result;
    }, { languagesIDs: [], universalConsentItemTranslations: [] });
    setInfo({ ...info, ...data });
    setLanguagesData(languagesData.filter(language => language.id !== id));
    if (id === chosenLanguage) {
      setChosenLanguage(data.languagesIDs[0]);
    }
  };

  const validate = ({ universalConsentItemTranslations }) => {
    const errors = { universalConsentItemTranslations: [] };
    universalConsentItemTranslations.forEach((item, i) => {
      if (item.shortText && item.shortText.length > 500) {
        errors.universalConsentItemTranslations[i] = {
          shortText: 'Short text can\'t be longer than 500 characters',
        };
      }
    });
    setTimeout(() => setIsInvalid(!!errors.universalConsentItemTranslations.length), 0);
    return errors;
  };

  /**
   * Checks if translations are already set
   * @param {UniversalConsentItemTypeTranslation} singleTranslation
   * @returns {boolean}
   */
  const containsTranslation = (singleTranslation) => {
    return singleTranslation.longText.length > 1 ||
      singleTranslation.shortText.length > 1;
  };

  const getItemType = (data) => {
    return data.universalConsentItemType.match(/\d+$/);
  };

  const getLanguageCodesToTranslate = (data) => {
    const languageCodes = {};
    for (const [key, singleTranslation] of Object.entries(data.universalConsentItemTranslations)) {
      if (containsTranslation(singleTranslation) === true) {
        continue;
      }
      languageCodes[`languageCode[${key}]`] = singleTranslation.language;
    }

    return languageCodes;
  };

  /**
   * Reads translations from API response Uint8Array
   * @param {Response} apiResponse
   * @returns {UniversalConsentItemTypeTranslation[]}
   */
  const readDefaultTypeTranslations = async(apiResponse) => {
    const dataStream = await apiResponse.body.getReader().read();

    return JSON.parse(new TextDecoder().decode(dataStream.value));
  };

  const fetchDefaultTranslations = async(data) => {
    const languageCodesToTranslate = getLanguageCodesToTranslate(data);

    if (Object.keys(languageCodesToTranslate).length === 0) {
      return;
    }

    const apiResponse = await sendCustomApiRequest(`/ucs/default_type_translations/${getItemType(data)}`, languageCodesToTranslate);

    if (apiResponse.ok === false) {
      console.error(apiResponse);
      return;
    }

    const defaultTypeTranslations = await readDefaultTypeTranslations(apiResponse);

    if (defaultTypeTranslations.length === 0) {
      return;
    }

    for (const key in data.universalConsentItemTranslations) {
      if (Object.hasOwnProperty.call(data.universalConsentItemTranslations, key) === false) {
        continue;
      }
      /** @type {UniversalConsentItemTypeTranslation} */
      const translationItem = data.universalConsentItemTranslations[key];
      const defaultTranslation = defaultTypeTranslations.find(element => element.language === translationItem.language);

      if (!defaultTranslation) {
        continue;
      }

      translationItem.longText = defaultTranslation.longText;
      translationItem.shortText = defaultTranslation.shortText;
    }
  };

  const fetchLanguagesNames = async() => {
    const languagesResponse = await dataProvider.getMany('languages', { ids: data.languagesIDs });
    setLanguagesData(languagesResponse.data);
    setChosenLanguage(languagesResponse.data[0].id);
  };

  useEffect(() => {
    setIsLoading(true);

    const loadForm = async() => {
      await fetchDefaultTranslations(data);
      setInfo(data);
      await fetchLanguagesNames();
    };

    loadForm()
      .finally(() => {
        setIsLoading(false);
      });

  }, [data]);

  return (
    <Form onSubmit={onSave}
      initialValues={info}
      validate={validate}
      render={({ handleSubmit, form, submitting, pristine, values }) =>
        isLoading ? <div className={classes.loader}><LoadingIndicator/></div> : (
          <div>
            <EditPageTitle padded onBack={() => onBack(values)} title={typeof info.id === 'undefined' ? 'Add content' : 'Edit content'}/>
            <div className={classes.wrapper}>
              <div className={classes.languagesWrapper}>
                <LanguagesList
                  chosenLanguage={chosenLanguage}
                  languages={languagesData}
                  onLanguagePick={onLanguagePick}
                  hasDelete={languagesData.length > 1}
                  onLanguageDelete={lang => onLanguageDelete(lang, values)}
                />
              </div>
              <form onSubmit={handleSubmit} className={classes.form}>
                {
                  values.universalConsentItemTranslations.map((translation, index) => (
                    <div className={chosenLanguage !== translation.language ? classes.hidden : ''} key={index}>
                      <LanguageFields index={index}/>
                    </div>
                  ))
                }
                <div className={classes.toolbar}>
                  <Button
                    color="secondary"
                    onClick={() => onBack(values)}
                    className={`${classes.button} ${classes.cancelButton}`}
                  >
                        Back
                  </Button>
                  <Button
                    color="secondary"
                    variant="contained"
                    className={`${classes.button} ${classes.saveButton}`}
                    disabled={isInvalid}
                    type="submit"
                  >
                        Save & publish
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}/>
  );
};
export default withStyles(styles)(withDataProvider(Languages));
